import { useState } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Button,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// hooks
import useAuth from '../../hooks/useAuth';
// components
import TodoHistoryPopoverComponent from './TodoHistoryPopoverComponent';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import { fKrDateKST } from '../../utils/formatTime';

// ----------------------------------------------------------------------

const ScrollContent = styled('div')(({ theme }) => ({
  maxHeight: 'calc(450px - 90px)',
  overflow: 'auto',
}));

export default function TodoHistoryPopover({ companyId }) {
  const { user } = useAuth();
  const { data, isValidating } = useSWR(user ? `/api/works/todo/crew/company/${companyId}/history/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const historyLabelColor = {
    create: 'info',
    delete: 'error',
    edit: 'oldprimary',
    done: 'primary',
    cancel: 'default',
  }

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <Button
        disableRipple
        sx={{
          ...(open && {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            '&:before': {
              zIndex: 1,
              content: "''",
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.5),
            },
          })
        }}
        variant="outlined"
        onClick={handleOpen}
        startIcon={
        <Iconify icon="mdi:clock-time-three-outline" />
      }>
        수정 내역
      </Button>

      <TodoHistoryPopoverComponent
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          '& .MuiMenuItem-root': {
            typography: 'body2',
          },
        }}
      >
        <Stack sx={{ position: 'fixed', width: '100%', zIndex: 10 }}>
          <Stack sx={{ px: 2.5, py: 2 }}>
            <Typography
              variant="subtitle1"
            >
              수정 내역
            </Typography>
          </Stack>
        </Stack>
        <Divider sx={{ pt: 7 }} />
        {isValidating && (
          <Stack spacing={2} sx={{ py: 2 }}>
            <Skeleton variant="rectangulur" height={40} />
            <Skeleton variant="rectangulur" height={40} />
            <Skeleton variant="rectangulur" height={40} />
          </Stack>
        )}
        {!isValidating && data?.history?.length === 0 && (
          <Typography sx={{ textAlign: 'center', py: 4 }} variant="subtitle2">
            수정 내역이 없습니다
          </Typography>
        )}
        {!isValidating && data?.history?.length > 0 && (
        <ScrollContent>
        {!isValidating && data?.history?.map((h, index) => (
          <Stack direction="row" key={`h-${index}`} sx={{ px: 2.5, py: 2 }} spacing={2} alignItems="initial">
            <Label
              sx={{ minWidth: '38px', ...h.history_type === 'cancel' && {whiteSpace: 'pre-wrap', height: '42px', lineHeight: 1.5} }}
              variant='filled'
              color={historyLabelColor[h.history_type]}
            >
              {
                (h.history_type === 'cancel' && '완료\n취소') ||
                (h.history_type === 'delete' && '삭제') ||
                (h.history_type === 'done' && '완료') ||
                (h.history_type === 'edit' && '수정') ||
                (h.history_type === 'create' && '추가')
              }
            </Label>
            <Stack spacing={.5}>
              <Typography
                variant="subtitle2"
                sx={{
                  wordBreak: 'break-all',
                  ...(h.history_type === 'done' || h.history_type === 'delete') && { textDecoration: 'line-through' },
                  ...h.history_type === 'delete' && { color: 'text.secondary' }
                }}
              >
                {h.content}
              </Typography>
              <Stack direction="row" sx={{ color: 'text.disabled' }} spacing={0.5} alignItems="center">
                <Iconify icon="mdi:clock-time-three-outline" />
                <Typography variant="caption">
                  {fKrDateKST(h.created_at)}
                </Typography>
              </Stack>
              {h.changed_crew && (
                <Stack direction="row" sx={{ color: 'text.disabled' }} spacing={0.5} alignItems="center">
                  <SvgIconStyle src={`/icons/ic_person.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="caption">
                    <b>{h.changed_crew.nickname}</b> 크루
                  </Typography>
                </Stack>
              )}
              {h.changed_company && (
                <Stack direction="row" sx={{ color: 'text.disabled' }} spacing={0.5} alignItems="center">
                  <SvgIconStyle src={`/icons/ic_person.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
                  <Typography variant="caption">
                    <b>{h.changed_company.name}</b>
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        ))}
        </ScrollContent>
        )}
        <Divider sx={{ mb: 4 }} />
      </TodoHistoryPopoverComponent>
    </>
  );
}
